import * as React from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import type {TreeSelectProps} from 'antd';
import {Spin, TreeSelect} from 'antd';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';
import {useDispatch, useSelector} from 'umi';

import {getBranchRequest, globalBranchesSearchRequest} from '@/services/api/location';
import * as LocationSelectors from '@/selectors/locationSelectors';
import {processAPIResponseNow} from '@/utils/request';
import {F, INTL_ACTION, INTL_ENTITY} from '@/utils/intl';
import {ServiceModel} from '@/typings/models/Location';
import {branchHasServices} from '@/components/Location/BranchServiceAgentCascader/utils';
import styles from "./index.less"

type BranchServiceWithBranch = ServiceModel & { branch?: any };

type BranchServiceSelectProps = {
  value?: string | number;
  branchId?: string | number;
  onChange?: (serviceId: string | undefined, service: BranchServiceWithBranch | null) => void;
  disabled?: boolean;
  placeholder?: React.ReactNode;
  selectProps?: Partial<TreeSelectProps>;
};

const BranchServiceSelect: React.FC<BranchServiceSelectProps> = ({
  value,
  branchId: preloadBranchId,
  onChange,
  disabled,
  placeholder,
  selectProps,
}) => {
  const dispatch = useDispatch();
  const allBranchesListFromStore = useSelector(LocationSelectors.selectAllBranchesList);
  const branchDetails = useSelector(LocationSelectors.selectBranchDetails);
  const branchDetailsRef = useRef(branchDetails);
  branchDetailsRef.current = branchDetails;
  const listLoading = useSelector(
    (state: DefaultRootState) => state.loading.effects['location/getAllBranchList'],
  );

  // services lazily loaded per branch id (key = branch id as string)
  const [loadedServices, setLoadedServices] = useState<Record<string, any[]>>({});
  const loadedServicesRef = useRef<Record<string, any[]>>({});

  // tree expand / loaded keys (controlled so branch stays open after load)
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  const [loadedKeys, setLoadedKeys] = useState<string[]>([]);

  // remote search state
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // null = not searching; otherwise: branchNameIds + servicesByBranch from API
  const [searchData, setSearchData] = useState<{
    branchNameIds: Set<any>;
    servicesByBranch: Map<any, any[]>;
  } | null>(null);

  useEffect(() => {
    if (isEmpty(allBranchesListFromStore)) {
      dispatch({type: 'location/getAllBranchList'});
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // --- lazy loading ---

  const fetchServicesForBranch = useCallback(async (branchId: string | number): Promise<void> => {
    const id = `${branchId}`;
    if (loadedServicesRef.current[id] !== undefined) return;

    const cached = branchDetailsRef.current[id];
    if (cached?.services) {
      const services = cached.services.filter((s: any) => !s.deleted_at);
      const next = { ...loadedServicesRef.current, [id]: services };
      loadedServicesRef.current = next;
      setLoadedServices(next);
      return;
    }

    const response = await getBranchRequest({params: {branchId: id}});
    const branch = response?.data?.data;
    const services = (branch?.services || []).filter((s: any) => !s.deleted_at);
    const next = {...loadedServicesRef.current, [id]: services};
    loadedServicesRef.current = next;
    setLoadedServices(next);
  }, []);

  // loadData receives the tree node object from Ant Design
  const handleLoadData = useCallback(
    async (node: any): Promise<void> => {
      const branchId = `${node.value}`.replace('branch_', '');
      await fetchServicesForBranch(branchId);
      setLoadedKeys(prev => (prev.includes(node.key) ? prev : [...prev, node.key]));
    },
    [fetchServicesForBranch],
  );

  // Pre-load the current appointment's branch so the selected value shows its label
  useEffect(() => {
    if (preloadBranchId) fetchServicesForBranch(preloadBranchId);
  }, [preloadBranchId]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- remote search (same logic as BranchServiceAgentCascader) ---

  const searchRemotely = useMemo(
    () =>
      debounce((term: string) => {
        if (!term.trim()) {
          setSearchData(null);
          setSearchLoading(false);
          return;
        }
        setSearchLoading(true);
        globalBranchesSearchRequest({query: {search: term}})
          .then(response => {
            processAPIResponseNow(
              response,
              {
                onSuccess(result: any) {
                  const {branches: foundBranches, services, users} = result;

                  const branchNameIds = new Set<any>(foundBranches?.map((b: any) => b.id));

                  // Collect matching service objects grouped by branch id
                  const servicesByBranch = new Map<any, any[]>();
                  const addService = (branchId: any, service: any) => {
                    if (!branchId || !service) return;
                    if (!servicesByBranch.has(branchId)) servicesByBranch.set(branchId, []);
                    const list = servicesByBranch.get(branchId)!;
                    if (!list.find((s: any) => s.id === service.id)) list.push(service);
                  };
                  services?.forEach((s: any) => addService(s?.branch?.id, s));
                  users?.forEach((u: any) =>
                    u?.services?.forEach((s: any) => addService(s?.branch?.id, s)),
                  );

                  // Also cache found services into loadedServicesRef so findService works after selection
                  servicesByBranch.forEach((svcs, branchId) => {
                    const id = `${branchId}`;
                    if (loadedServicesRef.current[id]) return; // don't overwrite full data with partial
                    const next = {...loadedServicesRef.current, [id]: svcs};
                    loadedServicesRef.current = next;
                    setLoadedServices(next);
                  });

                  setSearchData({branchNameIds, servicesByBranch});
                },
              },
              'data',
            );
          })
          .finally(() => setSearchLoading(false));
      }, 500),
    [], // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => () => searchRemotely.cancel(), [searchRemotely]);

  const handleSearch = useCallback(
    (term: string) => {
      setSearchTerm(term);
      if (!term.trim()) {
        setSearchData(null);
        searchRemotely.cancel();
      } else {
        searchRemotely(term);
      }
    },
    [searchRemotely],
  );

  // --- tree data ---

  const buildServiceNode = (branch: any, service: any) => ({
    title: service.service_type?.name,
    label: (
      <span>
        <span className="bss-branch-prefix">{branch.name} &gt;&nbsp;</span>
        {service.service_type?.name}
      </span>
    ),
    value: `${service.id}`,
    key: `${service.id}`,
    isLeaf: true,
  });

  // Default tree: all branches (minus those the API reports as having no
  // services), services lazy-loaded on expand
  const lazyTreeData = useMemo(
    () =>
      (allBranchesListFromStore || [])
        // Hide branches the API reports as having no services.
        .filter(branchHasServices)
        .map((branch: any) => {
        const services = loadedServices[`${branch.id}`];
        const hasLoaded = services !== undefined;
        return {
          title: branch.name,
          label: branch.name,
          value: `branch_${branch.id}`,
          key: `branch_${branch.id}`,
          selectable: false,
          isLeaf: hasLoaded && services.length === 0,
          ...(hasLoaded ? {
            children: services.map((s: any) => buildServiceNode(branch, s)),
          } : {}),
        };
      }),
    [allBranchesListFromStore, loadedServices],
  );

  // Search tree: only matching branches / services from API response
  const searchTreeData = useMemo(() => {
    if (!searchData) return null;
    const {branchNameIds, servicesByBranch} = searchData;
    const allBranchIds = new Set<any>([...branchNameIds, ...servicesByBranch.keys()]);

    return Array.from(allBranchIds)
      .map(branchId => {
        const branch = (allBranchesListFromStore || []).find((b: any) => b.id === branchId);
        if (!branch) return null;

        const matchingServices = servicesByBranch.get(branchId);
        if (matchingServices) {
          // Service-name match: show only the matched services
          return {
            title: branch.name,
            label: branch.name,
            value: `branch_${branch.id}`,
            key: `branch_${branch.id}`,
            selectable: false,
            isLeaf: matchingServices.length === 0,
            children: matchingServices
              .filter((s: any) => !s.deleted_at)
              .map((s: any) => buildServiceNode(branch, s)),
          };
        }
        // Branch-name match: show with loaded services or lazy-load arrow
        const loaded = loadedServices[`${branchId}`];
        return {
          title: branch.name,
          label: branch.name,
          value: `branch_${branch.id}`,
          key: `branch_${branch.id}`,
          selectable: false,
          isLeaf: loaded !== undefined && loaded.length === 0,
          ...(loaded !== undefined ? {
            children: loaded.map((s: any) => buildServiceNode(branch, s)),
          } : {}),
        };
      })
      .filter(Boolean);
  }, [searchData, allBranchesListFromStore, loadedServices]);

  const effectiveTreeData = searchTreeData ?? lazyTreeData;

  // --- selection ---

  const findService = useCallback(
    (serviceId: string | number): BranchServiceWithBranch | null => {
      for (const [branchId, services] of Object.entries(loadedServicesRef.current)) {
        const service = services.find((s: any) => `${s.id}` === `${serviceId}`);
        if (service) {
          const branch = (allBranchesListFromStore || []).find(
            (b: any) => `${b.id}` === branchId,
          );
          return {...service, branch};
        }
      }
      return null;
    },
    [allBranchesListFromStore],
  );

  const handleChange = useCallback(
    (serviceId: string) => {
      onChange?.(serviceId, serviceId ? findService(serviceId) : null);
    },
    [onChange, findService],
  );

  return (
    <Spin spinning={!!listLoading} size="small">
      <TreeSelect
        showSearch
        filterTreeNode={false}
        onSearch={handleSearch}
        loading={searchLoading}
        loadData={handleLoadData}
        treeLoadedKeys={loadedKeys}
        treeExpandedKeys={expandedKeys}
        onTreeExpand={(keys: any) => setExpandedKeys(keys as string[])}
        treeExpandAction="click"
        popupMatchSelectWidth={false}
        disabled={disabled}
        treeNodeLabelProp="label"
        value={value != null ? `${value}` : undefined}
        treeData={effectiveTreeData}
        onChange={handleChange}
        placeholder={
          placeholder || (
            <F entity={INTL_ENTITY.SERVICE} action={INTL_ACTION.SELECT} defaultMessage="Select Service" />
          )
        }
        rootClassName={styles.branchServiceSelect}
        popupClassName="bss-popup"
        dropdownStyle={{maxHeight: 400, overflow: 'auto'}}
        {...selectProps}
      />
    </Spin>
  );
};

export default BranchServiceSelect;
